import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import { Carpool } from "../components/Carpool";
import { MeetThread } from "../components/MeetThread";
import { useToast } from "../lib/toast";
import { EventLineupBoard } from "../components/EventLineupBoard";
import { Collapsible } from "../components/Collapsible";
import { InviteParentsPanel } from "../components/InviteParentsPanel";
import type { RsvpStatus } from "../api/types";

export function MeetDetailPage() {
  const { meetId = "" } = useParams();
  const meet = useAsync(() => api.getMeet(meetId), [meetId]);
  const rsvps = useAsync(() => api.listRsvps(meetId), [meetId]);
  const me = useAsync(() => api.getMe(), []);
  const wrestlers = useAsync(() => api.listWrestlers(), []);
  const myTeams = useAsync(() => api.listTeams(), []);

  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>();
  // Optimistic RSVP: reflects the chosen status immediately, overriding the
  // server value until the reload confirms (or an error rolls it back).
  const [optimisticRsvp, setOptimisticRsvp] = useState<RsvpStatus | undefined>();
  const [emails, setEmails] = useState("");
  const [calLink, setCalLink] = useState<string>();
  const [note, setNote] = useState("");
  const [noteSaved, setNoteSaved] = useState<string>();
  const [selectedKids, setSelectedKids] = useState<string[]>([]);
  const [hype, setHype] = useState<string>();
  useEffect(() => {
    let cancelled = false;
    api
      .getMeetHype(meetId)
      .then((r) => !cancelled && r.text && setHype(r.text))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [meetId]);

  // Pre-select kids from an existing RSVP when the meet loads.
  useEffect(() => {
    if (meet.data?.myRsvpWrestlerIds) {
      setSelectedKids(meet.data.myRsvpWrestlerIds);
    }
  }, [meet.data?.myRsvpWrestlerIds]);

  const toggleKid = (id: string) =>
    setSelectedKids((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  useEffect(() => {
    api
      .getNote(meetId)
      .then((n) => setNote(n.note))
      .catch(() => undefined);
  }, [meetId]);

  const saveNote = async () => {
    setNoteSaved(undefined);
    try {
      await api.saveNote(meetId, note);
      setNoteSaved("Saved");
      toast.success("Notes saved");
    } catch (e) {
      setNoteSaved((e as Error).message);
      toast.error((e as Error).message);
    }
  };

  const isAdmin =
    me.data?.teams.some(
      (t) => t.teamId === meet.data?.hostTeamId && t.role === "admin",
    ) ?? false;

  const rsvp = async (status: RsvpStatus) => {
    const prev = optimisticRsvp ?? (meet.data?.myRsvp as RsvpStatus | undefined);
    // Optimistic: reflect the new status instantly.
    setOptimisticRsvp(status);
    setBusy(true);
    setMsg(undefined);
    const label =
      status === "GOING" ? "Going" : status === "MAYBE" ? "Maybe" : "Not going";
    try {
      const res = await api.setRsvp(
        meetId,
        status,
        status === "NOT_GOING" ? [] : selectedKids,
      );
      toast.success(`RSVP updated — ${label}`);
      if (res.calendarHtmlLink) {
        toast.info("📅 Added to your Google Calendar");
      }
      meet.reload();
      rsvps.reload();
    } catch (e) {
      // Roll back the optimistic change.
      setOptimisticRsvp(prev);
      toast.error(`Couldn't update RSVP: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const addToCalendar = async () => {
    setMsg(undefined);
    try {
      const res = await api.addToCalendar(meetId, "create");
      setCalLink(res.htmlLink);
      setMsg("Added to Google Calendar.");
      toast.success("Added to Google Calendar");
    } catch (e) {
      setMsg((e as Error).message);
      toast.error((e as Error).message);
    }
  };

  const sendInvites = async () => {
    const list = emails
      .split(/[,\n]/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (list.length === 0) return;
    try {
      await api.createInvites(meetId, list);
      setEmails("");
      setMsg(`Sent ${list.length} invite(s).`);
      toast.success(`Sent ${list.length} invite(s)`);
    } catch (e) {
      setMsg((e as Error).message);
      toast.error((e as Error).message);
    }
  };

  const cancel = async () => {
    if (!confirm("Cancel this meet? Attendees will be notified.")) return;
    try {
      await api.cancelMeet(meetId);
      toast.info("Meet cancelled — attendees will be notified");
      meet.reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const setMeetType = async (meetType: "individual" | "dual") => {
    if (meet.data?.meetType === meetType) return;
    try {
      await api.updateMeet(meetId, { meetType });
      toast.success(
        meetType === "dual"
          ? "Set to Dual — lineup board enabled"
          : "Set to Individual — no weight-class lineup",
      );
      meet.reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const toggleInvitedTeam = async (teamId: string) => {
    const current = meet.data?.invitedTeamIds ?? [];
    const next = current.includes(teamId)
      ? current.filter((t) => t !== teamId)
      : [...current, teamId];
    try {
      await api.setMeetTeams(meetId, next);
      toast.success(
        current.includes(teamId) ? "Team removed" : "Team invited",
      );
      meet.reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  if (meet.loading) return <p>Loading…</p>;
  if (meet.error) return <p className="error">{meet.error}</p>;
  const m = meet.data!;

  return (
    <div>
      <div className="row spread">
        <h1>{m.title}</h1>
        {m.status === "cancelled" && (
          <span className="badge cancelled">cancelled</span>
        )}
      </div>
      <p className="muted">
        <span className="badge accent" style={{ marginRight: "0.4rem" }}>
          {m.meetType === "dual" ? "Dual meet" : "Individual"}
        </span>
        {new Date(m.startAt).toLocaleString()} — {new Date(m.endAt).toLocaleTimeString()}
      </p>

      {hype && m.status !== "cancelled" && (
        <div className="meet-hype">
          <span className="bolt" aria-hidden="true">⚡</span>
          <span>{hype}</span>
        </div>
      )}

      <div className="card">
        <strong>{m.venueName}</strong>
        <p className="muted">
          {m.address.line1}, {m.address.city}, {m.address.state} {m.address.zip}
          {typeof m.distanceMiles === "number" && ` · ${m.distanceMiles} mi away`}
        </p>
        {m.directionsUrl && (
          <a href={m.directionsUrl} target="_blank" rel="noreferrer">
            Get directions
          </a>
        )}
        {m.description && <p>{m.description}</p>}
        {(m.format || m.sessionDetails) && (
          <p className="muted">
            {m.format && <span className="badge">{m.format}</span>}{" "}
            {m.sessionDetails}
          </p>
        )}
        {m.dateTentative && (
          <p className="muted">⚠️ Date is tentative/projected.</p>
        )}
        {(m.registrationInfo || m.registrationUrl) && (
          <p className="muted">
            Registration: {m.registrationInfo}{" "}
            {m.registrationUrl && (
              <a href={m.registrationUrl} target="_blank" rel="noreferrer">
                Register
              </a>
            )}
          </p>
        )}
      </div>

      <div className="meet-grid">
        <div className="meet-col meet-col-main">

      {m.status !== "cancelled" && (
        <div className="card rsvp-card">
          {(() => {
            const current = optimisticRsvp ?? (m.myRsvp as RsvpStatus | undefined);
            const label =
              current === "GOING"
                ? "Going"
                : current === "MAYBE"
                  ? "Maybe"
                  : current === "NOT_GOING"
                    ? "Can't make it"
                    : undefined;
            const pillClass =
              current === "GOING"
                ? "going"
                : current === "NOT_GOING"
                  ? "cancelled"
                  : current === "MAYBE"
                    ? "state"
                    : "";
            return (
              <div className="row spread">
                <h2 style={{ margin: 0 }}>Your RSVP</h2>
                <span className={`badge ${pillClass}`}>
                  {label ? label : "No response"}
                </span>
              </div>
            );
          })()}
          <p className="muted" style={{ marginTop: "0.25rem", fontSize: "0.85rem" }}>
            Tells your team you&apos;re attending — not a tournament registration.
            {m.registrationUrl && (
              <>
                {" "}
                <a href={m.registrationUrl} target="_blank" rel="noreferrer">
                  Register →
                </a>
              </>
            )}
          </p>
          {wrestlers.data && wrestlers.data.items.length > 0 ? (
            <div style={{ marginBottom: "0.6rem" }}>
              <p className="muted" style={{ fontSize: "0.85rem", marginBottom: "0.3rem" }}>
                Who&apos;s attending?
              </p>
              <div className="grid">
                {wrestlers.data.items.map((w) => (
                  <label key={w.wrestlerId} className="row" style={{ gap: "0.4rem" }}>
                    <input
                      type="checkbox"
                      style={{ width: "auto" }}
                      checked={selectedKids.includes(w.wrestlerId)}
                      onChange={() => toggleKid(w.wrestlerId)}
                    />
                    <span>
                      {w.displayName}
                      {w.weightClass && (
                        <span className="muted"> · {w.weightClass}</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}
          <div className="rsvp-buttons">
            {(() => {
              const current =
                optimisticRsvp ?? (m.myRsvp as RsvpStatus | undefined);
              const btn = (
                status: RsvpStatus,
                text: string,
                icon: string,
                tone: string,
              ) => {
                const active = current === status;
                return (
                  <button
                    className={`rsvp-choice ${tone}${active ? " active" : ""}${
                      active ? " rsvp-pulse" : ""
                    }`}
                    disabled={busy}
                    aria-pressed={active}
                    onClick={() => rsvp(status)}
                  >
                    <span aria-hidden="true">{icon}</span> {text}
                  </button>
                );
              };
              return (
                <>
                  {btn("GOING", "Going", "✅", "go")}
                  {btn("MAYBE", "Maybe", "❔", "maybe")}
                  {btn("NOT_GOING", "Can't make it", "✖", "no")}
                </>
              );
            })()}
          </div>
        </div>
      )}

      <div className="card">
        <h2>Who's going</h2>
        {rsvps.loading && <p>Loading…</p>}
        {rsvps.data && (
          <>
            <p className="muted">
              Going {rsvps.data.counts.going} · Maybe {rsvps.data.counts.maybe}
            </p>
            <div className="grid">
              {rsvps.data.going.map((a) => (
                <span key={a.userId}>
                  ✅ {a.displayName ?? a.userId}
                  {a.wrestlerNames && a.wrestlerNames.length > 0 && (
                    <span className="muted"> — {a.wrestlerNames.join(", ")}</span>
                  )}
                </span>
              ))}
              {rsvps.data.maybe.map((a) => (
                <span key={a.userId} className="muted">
                  ❔ {a.displayName ?? a.userId}
                  {a.wrestlerNames && a.wrestlerNames.length > 0 && (
                    <span> — {a.wrestlerNames.join(", ")}</span>
                  )}
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      <Collapsible title="Carpools" icon="🚗">
        <Carpool meetId={meetId} myUserId={me.data?.userId} />
      </Collapsible>

      <Collapsible title="Discussion" icon="💬">
        <MeetThread meetId={meetId} myUserId={me.data?.userId} />
      </Collapsible>

      {(m.meetType === "dual" ||
        (!m.meetType && /dual/i.test(m.format ?? ""))) && (
        <EventLineupBoard
          meetId={meetId}
          isAdmin={isAdmin}
          meetTitle={m.title}
          meetDate={m.startAt}
        />
      )}

        </div>{/* /meet-col-main */}

        <div className="meet-col meet-col-side">

      <Collapsible title="My notes (private)" icon="📝">
        <p className="muted">
          Only you can see these — carpool plans, weigh-in time, what to pack.
        </p>
        <textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Your private notes for this meet…"
        />
        <div className="row" style={{ marginTop: "0.5rem" }}>
          <button onClick={saveNote}>Save notes</button>
          {noteSaved && <span className="muted">{noteSaved}</span>}
        </div>
      </Collapsible>

      <Collapsible title="Add to calendar" icon="📅">
        <div className="row">
          {me.data?.googleLinked ? (
            <button onClick={addToCalendar}>Add to Google Calendar</button>
          ) : (
            <span className="muted">
              Link Google in your profile to add with one tap.
            </span>
          )}
          <a href={api.icsUrl(meetId)} target="_blank" rel="noreferrer">
            Download .ics
          </a>
          {calLink && (
            <a href={calLink} target="_blank" rel="noreferrer">
              View event
            </a>
          )}
        </div>
      </Collapsible>

      <Collapsible title="Invite someone not on the team (email)" icon="✉️">
        <textarea
          rows={2}
          placeholder="Email addresses, comma or newline separated"
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
        />
        <div style={{ marginTop: "0.5rem" }}>
          <button onClick={sendInvites}>Send invites</button>
        </div>
      </Collapsible>

      {isAdmin && m.status !== "cancelled" && (
        <div className="card">
          <h2>Admin</h2>

          <div style={{ margin: "0.5rem 0" }}>
            <h3 style={{ marginBottom: "0.25rem" }}>Meet type</h3>
            <div className="seg" role="radiogroup" aria-label="Meet type">
              <button
                role="radio"
                aria-checked={m.meetType === "dual"}
                className={m.meetType === "dual" ? "seg-on" : ""}
                onClick={() => setMeetType("dual")}
              >
                Dual
              </button>
              <button
                role="radio"
                aria-checked={m.meetType === "individual"}
                className={m.meetType === "individual" ? "seg-on" : ""}
                onClick={() => setMeetType("individual")}
              >
                Individual
              </button>
            </div>
            <p className="muted" style={{ fontSize: "0.82rem", marginTop: "0.3rem" }}>
              {m.meetType === "dual"
                ? "Dual: shows a weight-class lineup board."
                : "Individual: no weight-class lineup (tournament/open)."}
            </p>
          </div>

          <div style={{ margin: "0.5rem 0" }}>
            <h3 style={{ marginBottom: "0.25rem" }}>Invited teams</h3>
            <div className="row" style={{ flexWrap: "wrap", gap: "0.4rem" }}>
              {(myTeams.data?.items ?? [])
                .filter((t) => t.teamId !== m.hostTeamId && !t.archived)
                .map((t) => {
                  const invited = (m.invitedTeamIds ?? []).includes(t.teamId);
                  return (
                    <button
                      key={t.teamId}
                      className={invited ? "" : "secondary"}
                      aria-pressed={invited}
                      onClick={() => toggleInvitedTeam(t.teamId)}
                    >
                      {invited ? "✓ " : "+ "}
                      {t.name ?? t.teamId.slice(0, 8)}
                    </button>
                  );
                })}
              {(myTeams.data?.items ?? []).filter(
                (t) => t.teamId !== m.hostTeamId && !t.archived,
              ).length === 0 && (
                <span className="muted" style={{ fontSize: "0.85rem" }}>
                  No other teams to invite.
                </span>
              )}
            </div>
          </div>

          <button className="danger" onClick={cancel}>
            Cancel meet
          </button>
        </div>
      )}

      {!isAdmin && m.status !== "cancelled" && (
        <p className="muted" style={{ fontSize: "0.82rem" }}>
          Only team admins can edit or cancel this meet.
        </p>
      )}

        </div>{/* /meet-col-side */}
      </div>{/* /meet-grid */}

      {isAdmin && m.status !== "cancelled" && (
        <div className="card">
          <h2>👥 Invite your team</h2>
          <InviteParentsPanel meetId={meetId} />
        </div>
      )}

      {msg && <p className="muted">{msg}</p>}
    </div>
  );
}
